import * as React from "react";
import { useSelector } from "react-redux";
import { SpinnerBlock } from "../common/Spinner";
import * as authSelectors from "../auth/authSelectors";
import { routes } from "../../appRoutingHelpers";
import { MemberCardTypeUpsertForm } from "./MemberCardTypeUpsertForm";
import { useNavigate, useParams } from "react-router-dom";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import {
  useCreateMembercardType,
  useGetMembercardType,
  useGetOrganizationTerms,
  useUpdateMembercardType,
  useUploadMembercardImage,
} from "../../service/api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
interface Props {}

const MemnerCardTypeUpsert: React.FC<Props> = (props) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    data: memberCardType,
    isLoading: isLoadingMemberCardType,
    error: getMembercardTypeError,
  } = useGetMembercardType(selectedOrganizationKeyName, id);

  const {
    data: userTerms,
    isLoading: isLoadingUserTerms,
    error: getOrganizationTermsError,
  } = useGetOrganizationTerms(selectedOrganizationKeyName);

  const {
    mutateAsync: uploadMembercardImage,
    isLoading: isUploadingMembercardImage,
  } = useUploadMembercardImage();

  const {
    mutateAsync: createMemberCard,
    isLoading: isCreatingMembercard,
  } = useCreateMembercardType();

  const {
    mutateAsync: updateMemberCard,
    isLoading: isUpdatingMembercard,
  } = useUpdateMembercardType(id);

  const onSubmit = async (
    values: any,
    backgroundFile: File | null,
    logoFile: File | null
  ) => {
    let memberCardId = isEdit ? id : null;

    if (
      values.memberCardTypeDiscount.appliesToCollectionMethods &&
      values.memberCardTypeDiscount.appliesToCollectionMethods.length === 0
    ) {
      values.memberCardTypeDiscount.appliesToCollectionMethods = null;
    }

    try {
      if (isEdit) {
        await updateMemberCard({
          data: values,
          organizationKeyName: selectedOrganizationKeyName,
          id,
        });
      } else {
        const memberCard = await createMemberCard({
          data: values,
          organizationKeyName: selectedOrganizationKeyName,
        });
        memberCardId = memberCard.id;
      }
      if (!memberCardId) {
        throw new Error("No membercard type id found");
      }
      if (logoFile) {
        await uploadMembercardImage({
          file: logoFile,
          organizationKeyName: selectedOrganizationKeyName,
          type: "logo",
          id: memberCardId,
        });
      }
      if (backgroundFile) {
        await uploadMembercardImage({
          file: backgroundFile,
          organizationKeyName: selectedOrganizationKeyName,
          type: "background",
          id: memberCardId,
        });
      }
      navigate(routes.memberCardTypes(selectedOrganizationKeyName));
      toastr.success(
        "Success",
        `Member card type ${isEdit ? "updated" : "created"}`
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(getMembercardTypeError);
  useHandleFetchError(getOrganizationTermsError);

  if (isLoadingMemberCardType || isLoadingUserTerms) {
    return <SpinnerBlock />;
  }

  if (isEdit && !memberCardType) {
    return null;
  }

  return (
    <div className="container mt-5">
      <GoBackLinkButton
        to={routes.memberCardTypes(selectedOrganizationKeyName)}
        text="Back to all member card templates"
      />
      <div className="row">
        <div className="col-12">
          <MemberCardTypeUpsertForm
            onSubmit={onSubmit}
            memberCardType={isEdit ? memberCardType || {} : {}}
            isUpsertingMemberCardType={
              isCreatingMembercard ||
              isUpdatingMembercard ||
              isUploadingMembercardImage
            }
            userTerms={userTerms || []}
          />
        </div>
      </div>
    </div>
  );
};

export default MemnerCardTypeUpsert;
